import { sv } from "@dhx/ts-common/dom";
import { getCoordinates } from "../helpers/circle";
import { calcPointRef, locator } from "../helpers/common";
import { IRadarConfig, IScale, PointData, ScaleType, SvgElement } from "../types";
import { IFitPosition } from "@dhx/ts-common/html";
import BaseSeria from "./BaseSeria";

export default class Radar extends BaseSeria {
	public config: IRadarConfig;
	private _scale;
	private _locator;

	addScale(type: ScaleType, scale: IScale): void {
		this._scale = scale;
	}

	scaleReady(sizes: IFitPosition) {
		for (const key in sizes) {
			sizes[key] += this.config.paddings;
		}
		return sizes;
	}

	dataReady(prev?: PointData[]) {
		if (!this.config.active) {
			return (this._points = []);
		}

		const xLocator = locator(this._scale.config.value);
		this._points = this._data.map((item, index) => {
			// raw values
			const value = this._locator(item);
			const set = [value, value, item.id, value, value];
			if (prev) {
				set[1] += prev[index][1];
			}
			if (xLocator) {
				set.push(xLocator(item));
			}
			return set;
		});

		return this._points;
	}

	getTooltipText(id: string) {
		if (this.config.tooltip) {
			const p = this._defaultLocator(this._data.getItem(id));
			if (this.config.tooltipTemplate) {
				return this.config.tooltipTemplate(p);
			}
			return p;
		}
	}

	paint(width: number, height: number): SvgElement {
		super.paint(width, height);
		const getPointAriaAttrs = (key, points) => {
			let point;
			if (key) {
				point = points.find(p => key.includes(p[2]));
			}

			return {
				role: "graphics-symbol",
				"aria-roledescription": "point",
				"aria-label": point ? `point x=${point[5]} y=${point[4]}` : "",
				tabindex: 0,
			};
		};
		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.value || ""}`,
		});

		if (!this.config.active) {
			return;
		}

		const config = this.config;
		const svg = [];

		const d =
			this._points.map((item, first) => (first ? "L" : "M") + `${item[0]} ${item[1]}`).join(" ") + "Z";
		svg.push(
			sv("path", {
				d,
				stroke: config.color,
				"stroke-width": config.strokeWidth,
				fill: config.fill,
				"fill-opacity": config.alpha,
				class: "chart radar",
			})
		);
		if (config.pointType) {
			const points = this._points
				.map(p => this._drawPointType(p[0], p[1], calcPointRef(p[2], this.id)))
				.map(node => {
					if (node && node.attrs) {
						node.attrs = { ...node.attrs, ...getPointAriaAttrs(node.key, this._points) };
					}
					return node;
				});
			svg.push(sv("g", points));
		}
		return sv(
			"g",
			{
				id: "seria" + config.id,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
			},
			svg
		);
	}

	protected _calckFinalPoints(width: number, height: number) {
		let radius;
		if (height > width) {
			radius = width / 2;
		} else {
			radius = height / 2;
		}
		const scalePercent = 1 / this._data.getLength();
		let currentPercent = -0.25 - scalePercent;

		this._points.forEach((item, index) => {
			currentPercent += scalePercent;
			const value = this._scale.point(item[0]);
			const real = getCoordinates(currentPercent, value * radius, value * radius);

			// scaled values
			item[0] = real[0] + width / 2;
			item[1] = real[1] + height / 2;
		});
	}

	protected _defaultLocator(v) {
		return this._locator(v);
	}

	protected _setDefaults(config: IRadarConfig): void {
		const defaults = {
			strokeWidth: 2,
			active: true,
			tooltip: true,
			paddings: 5,
			color: "none",
			fill: "none",
			pointType: "circle",
		};

		this._locator = locator(config.value);

		config.scales = config.scales || ["radial"];

		this.config = { ...defaults, ...config } as IRadarConfig;
		if (this.config.pointType) {
			const color = this.config.pointColor || this.config.color;
			this._drawPointType = this._getPointType(this.config.pointType, color);
		}
	}
}
